import { FC, useState, useEffect } from "react";
import { TextInput } from "@jugl-web/ui-components/cross-platform/forms/TextInput";
import { ReactComponent as ChevronDownIcon } from "./assets/chevron-down.svg";
import { ReactComponent as ChevronUpIcon } from "./assets/chevron-up.svg";

export interface InputNumberPixelProps {
  label: string;
  value: number;
  onChange?: (value: number) => void;
}

const MIN = 20;
const MAX = 80;
const MAX_LENGTH = 4;

export const InputNumberPixel: FC<InputNumberPixelProps> = ({
  label,
  value,
  onChange,
}) => {
  const [inputValue, setInputValue] = useState(value.toString());

  useEffect(() => {
    setInputValue(value.toString());
  }, [value]);

  const clampValue = (numValue: number): number => {
    return Math.max(MIN, Math.min(MAX, numValue));
  };

  const handleInputChange = (newValue: string) => {
    if (newValue.length > MAX_LENGTH) {
      return;
    }

    setInputValue(newValue);
  };

  const handleBlur = () => {
    const numValue = parseInt(inputValue, 10);

    if (Number.isNaN(numValue) || inputValue.trim() === "") {
      const clampedValue = MIN;
      setInputValue(clampedValue.toString());
      onChange?.(clampedValue);
      return;
    }

    const clampedValue = clampValue(numValue);
    setInputValue(clampedValue.toString());

    if (clampedValue !== value) {
      onChange?.(clampedValue);
    }
  };

  const handleIncrement = () => {
    const numValue = parseInt(inputValue, 10) || value;
    const newValue = clampValue(numValue + 1);
    setInputValue(newValue.toString());
    onChange?.(newValue);
  };

  const handleDecrement = () => {
    const numValue = parseInt(inputValue, 10) || value;
    const newValue = clampValue(numValue - 1);
    setInputValue(newValue.toString());
    onChange?.(newValue);
  };

  const currentNumValue = parseInt(inputValue, 10) || value;
  const isAtMax = currentNumValue >= MAX;
  const isAtMin = currentNumValue <= MIN;

  return (
    <div className="relative">
      <TextInput
        labelPosition="border-overlap"
        label={label}
        type="number"
        value={inputValue}
        onChange={(e) => handleInputChange(e.target.value)}
        onBlur={handleBlur}
        classNames={{ inputClassName: "jugl__hide-number-arrows" }}
        endContent={
          <div className="flex flex-col ">
            <button
              type="button"
              onClick={handleIncrement}
              disabled={isAtMax}
              className="text-dark-600 hover:bg-grey-100 flex cursor-pointer items-center justify-center rounded border-none bg-transparent p-1 leading-none transition-opacity disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
              aria-label="Increase"
            >
              <ChevronUpIcon />
            </button>
            <button
              type="button"
              onClick={handleDecrement}
              disabled={isAtMin}
              className="text-dark-600 hover:bg-grey-100 flex cursor-pointer items-center justify-center rounded border-none bg-transparent p-1 leading-none transition-opacity disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
              aria-label="Decrease"
            >
              <ChevronDownIcon />
            </button>
          </div>
        }
      />
      <span className="text-dark-600 font-secondary absolute left-16 bottom-[18px] transform text-sm">
        px
      </span>
    </div>
  );
};
